/**
 Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DailyReport, DailyReportQuery } from "~/types/DailyReport";
import dailyReportService from "~/services/daily-report-service";

type initState = {
  data: DailyReport[]; // timeline list, newest first
  selectedReportId: string | null;
  loading: boolean;
};

export const initialState: initState = {
  data: [],
  selectedReportId: null,
  loading: true,
};

export const fetchDailyReports = createAsyncThunk(
  "dailyReport/fetchDailyReports",
  async (query: DailyReportQuery) => {
    const response = await dailyReportService.getDailyReports(query);
    return response;
  }
);

const dailyReportSlice = createSlice({
  name: "dailyReport",
  initialState,
  reducers: {
    setSelectedReportId: (state, action) => {
      state.selectedReportId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDailyReports.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchDailyReports.fulfilled, (state, action) => {
      state.loading = false;
      // Keep the existing array reference when nothing material changed, so the
      // generation poll (refetch every 10s) doesn't re-render the whole page —
      // replacing data with identical content re-parses every markdown finding
      // and shows as screen flicker (MCS-4151).
      const fingerprint = (rows: DailyReport[]) =>
        rows.map((r) => `${r.id}:${r.generatedAt ?? 0}:${r.updationTime ?? 0}`).join("|");
      if (fingerprint(state.data as DailyReport[]) !== fingerprint(action.payload)) {
        state.data = action.payload;
      }
      // Default selection: newest report (data is returned newest-first).
      if (!state.selectedReportId && action.payload.length > 0) {
        state.selectedReportId = action.payload[0].id;
      }
    });
    builder.addCase(fetchDailyReports.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const dailyReportAction = dailyReportSlice.actions;

export default dailyReportSlice.reducer;
